'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import { fetchMe, fetchMeStats, updateMyPhone } from '@/features/session/api/session.api'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

export function useMe() {
  const authedFetch = useApiFetch()
  return useQuery({ queryKey: queryKeys.session.me, queryFn: () => fetchMe(authedFetch) })
}

/** Self-service — the field the Call button on a case is gated on. Invalidates `useMe`'s own
 * cache rather than `setQueryData`, since the 204 response carries nothing to write in. */
export function useUpdateMyPhone() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: (phone: string) => updateMyPhone(authedFetch, phone),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.session.me })
      toast({ variant: 'success', title: 'Mobile number saved' })
    },
    onError: (error) =>
      toast({ variant: 'destructive', title: AppError.fromUnknown(error).message }),
  })
}

export function useMeStats() {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.session.meStats,
    queryFn: () => fetchMeStats(authedFetch),
  })
}
